// @ts-check
/** @typedef {import('../types/api').Vendor} Vendor */

import { Building2, Edit, Eye, Plus, RefreshCw, Search, Trash2, TrendingUp, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';
import Modal from '../components/ui/Modal';
import { useToast } from '../context/ToastContext';
import vendorService from '../services/vendorService';

const VendorsPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  /** @type {[Vendor[], Function]} */
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    vendor_type: '',
    status: '',
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);

  const vendorTypes = [
    { value: 'fabric_supplier', label: 'Fabric Supplier' },
    { value: 'manufacturer', label: 'Manufacturer' },
    { value: 'logistics', label: 'Logistics' },
    { value: 'packaging', label: 'Packaging' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'printing_dyeing', label: 'Printing & Dyeing' },
  ];

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const data = await vendorService.getVendors(filters);
      setVendors(data);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showToast]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor? (Soft delete - can be restored)')) return;

    try {
      await vendorService.deleteVendor(vendorId, false);
      showToast('Vendor deleted successfully', 'success');
      fetchVendors();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const getVendorTypeLabel = (type) => {
    const found = vendorTypes.find((t) => t.value === type);
    return found ? found.label : type;
  };

  const getRatingColor = (rating) => {
    if (!rating) return 'text-muted-foreground/70';
    if (rating >= 4) return 'text-green-600 dark:text-green-400';
    if (rating >= 3) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Vendor Management</h1>
          <p className="text-muted-foreground mt-1">Manage your suppliers, manufacturers, and vendors</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchVendors}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-3 border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 transition"
          >
            <Plus size={20} />
            Add Vendor
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70" size={20} />
            <input
              type="text"
              placeholder="Search vendors..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-10 pr-10 w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
            {filters.search && (
              <button
                onClick={() => setFilters({ ...filters, search: '' })}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70 hover:text-foreground transition-colors"
                title="Clear search"
              >
                <X size={18} />
              </button>
            )}
          </div>

          <select
            value={filters.vendor_type}
            onChange={(e) => setFilters({ ...filters, vendor_type: e.target.value })}
            className="px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
          >
            <option value="">All Types</option>
            {vendorTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>

          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button
            onClick={() => setFilters({ search: '', vendor_type: '', status: '' })}
            className="flex items-center justify-center gap-2 px-4 py-2 border border-input rounded-lg hover:bg-muted/50 transition"
          >
            <RefreshCw size={16} />
            Reset
          </button>
        </div>
      </div>

      {/* Vendors Grid */}
      {loading ? (
        <LoadingScreen />
      ) : vendors.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-12 text-center">
          <Building2 className="mx-auto text-muted-foreground/70 mb-4" size={48} />
          <h3 className="text-xl font-semibold text-foreground mb-2">No vendors found</h3>
          <p className="text-muted-foreground mb-6">Get started by adding your first vendor</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 transition"
          >
            Add Vendor
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vendors.map((vendor) => (
            <div key={vendor.id} className="bg-card rounded-lg shadow hover:shadow-lg transition p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-bold text-foreground truncate">{vendor.vendor_name}</h3>
                    <span
                      className={`px-2 py-1 text-xs rounded-full flex-shrink-0 ${
                        vendor.status === 'active'
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                          : 'bg-muted text-foreground'
                      }`}
                    >
                      {vendor.status}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{vendor.company_name}</p>
                  <p className="text-xs text-muted-foreground mt-1">{getVendorTypeLabel(vendor.vendor_type)}</p>
                </div>
                <span className="bg-muted text-foreground px-3 py-1 rounded-lg font-mono text-sm font-semibold flex-shrink-0 ml-2">
                  {vendor.vendor_initials}
                </span>
              </div>

              {/* Ratings */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-4 pt-4 border-t">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">Quality</p>
                  <p className={`font-bold ${getRatingColor(vendor.quality_rating)}`}>
                    {vendor.quality_rating ? parseFloat(String(vendor.quality_rating)).toFixed(1) : 'N/A'}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">Delivery</p>
                  <p className={`font-bold ${getRatingColor(vendor.delivery_rating)}`}>
                    {vendor.delivery_rating ? parseFloat(String(vendor.delivery_rating)).toFixed(1) : 'N/A'}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">Response</p>
                  <p className={`font-bold ${getRatingColor(vendor.response_rating)}`}>
                    {vendor.response_rating ? parseFloat(String(vendor.response_rating)).toFixed(1) : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Contact */}
              {(vendor.email || vendor.phone) && (
                <div className="space-y-1 mb-4 pb-4 border-b text-sm">
                  {vendor.email && <p className="text-muted-foreground truncate">📧 {vendor.email}</p>}
                  {vendor.phone && <p className="text-muted-foreground truncate">📞 {vendor.phone}</p>}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/vendors/${vendor.id}`)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition text-sm"
                >
                  <Eye size={16} />
                  View
                </button>
                <button
                  onClick={() => navigate(`/vendors/${vendor.id}/analytics`)}
                  className="flex items-center justify-center px-3 py-2 border border-input rounded-lg hover:bg-muted/50 transition"
                  title="Analytics"
                >
                  <TrendingUp size={16} />
                </button>
                <button
                  onClick={() => {
                    setEditingVendor(vendor);
                    setShowCreateModal(true);
                  }}
                  className="flex items-center justify-center px-3 py-2 border border-input rounded-lg hover:bg-muted/50 transition"
                  title="Edit"
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => handleDeleteVendor(vendor.id)}
                  className="flex items-center justify-center px-3 py-2 border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/70 transition"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <VendorFormModal
          vendor={editingVendor}
          vendorTypes={vendorTypes}
          onClose={() => {
            setShowCreateModal(false);
            setEditingVendor(null);
          }}
          onSuccess={() => {
            setShowCreateModal(false);
            setEditingVendor(null);
            fetchVendors();
          }}
        />
      )}
    </div>
  );
};

// Vendor Form Modal Component
const VendorFormModal = ({ vendor, vendorTypes, onClose, onSuccess }) => {
  const { showToast } = useToast();
  // Touched-since-open flag, reset after a successful save.
  const [isDirty, setIsDirty] = useState(false);
  const [formData, setFormDataRaw] = useState({
    vendor_name: vendor?.vendor_name || '',
    company_name: vendor?.company_name || '',
    vendor_initials: vendor?.vendor_initials || '',
    contact_person: vendor?.contact_person || '',
    email: vendor?.email || '',
    phone: vendor?.phone || '',
    phone_country_code: vendor?.phone_country_code || '',
    address: vendor?.address || '',
    vendor_type: vendor?.vendor_type || 'fabric_supplier',
    gst_number: vendor?.gst_number || '',
    pan_number: vendor?.pan_number || '',
    preferred_payment_method: vendor?.preferred_payment_method || '',
    status: vendor?.status || 'active',
    notes: vendor?.notes || '',
  });
  const setFormData = useCallback((updater) => {
    setIsDirty(true);
    setFormDataRaw(updater);
  }, []);
  const [saving, setSaving] = useState(false);

  // X icon and footer Cancel both dismiss the modal — confirm first if
  // there's anything to lose.
  const handleCloseClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (vendor) {
        await vendorService.updateVendor(vendor.id, formData);
        showToast('Vendor updated successfully', 'success');
      } else {
        await vendorService.createVendor(formData);
        showToast('Vendor created successfully', 'success');
      }
      setIsDirty(false);
      onSuccess();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const generateInitials = () => {
    if (formData.vendor_name) {
      const words = formData.vendor_name.trim().toUpperCase().split(' ');
      const initials =
        words.length === 1
          ? words[0].substring(0, 3)
          : words
              .slice(0, 4)
              .map((w) => w[0])
              .join('');
      setFormData({ ...formData, vendor_initials: initials });
    }
  };

  return (
    <Modal
      maxWidth="2xl"
      onClose={handleCloseClick}
      className="rounded-lg"
      closeOnBackdrop={false}
      hasUnsavedChanges={isDirty}
    >
      <div className="sticky top-0 bg-card border-b px-6 py-4 flex justify-between items-center">
        <h2 className="text-2xl font-bold">{vendor ? 'Edit Vendor' : 'Add New Vendor'}</h2>
        <button onClick={handleCloseClick} className="text-muted-foreground hover:text-foreground">
          ✕
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-foreground/80 mb-1">Vendor Name *</label>
            <input
              type="text"
              required
              value={formData.vendor_name}
              onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-foreground/80 mb-1">Company Name *</label>
            <input
              type="text"
              required
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Vendor Initials *</label>
            <div className="flex gap-2">
              <input
                type="text"
                required
                value={formData.vendor_initials}
                onChange={(e) => setFormData({ ...formData, vendor_initials: e.target.value.toUpperCase() })}
                className="flex-1 px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent font-mono"
                maxLength={10}
              />
              <button
                type="button"
                onClick={generateInitials}
                className="px-4 py-2 bg-muted text-foreground/80 rounded-lg hover:bg-accent transition text-sm"
              >
                Auto
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Used for order numbering (e.g., KD-2026-001)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Vendor Type *</label>
            <select
              required
              value={formData.vendor_type}
              onChange={(e) => setFormData({ ...formData, vendor_type: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            >
              {vendorTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Contact Person</label>
            <input
              type="text"
              value={formData.contact_person}
              onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Phone</label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="+1"
                value={formData.phone_country_code}
                onChange={(e) => setFormData({ ...formData, phone_country_code: e.target.value })}
                className="w-20 px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent text-sm"
              />
              <input
                type="tel"
                placeholder="Phone number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="flex-1 px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">GST Number</label>
            <input
              type="text"
              value={formData.gst_number}
              onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">PAN Number</label>
            <input
              type="text"
              value={formData.pan_number}
              onChange={(e) => setFormData({ ...formData, pan_number: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Payment Method</label>
            <input
              type="text"
              value={formData.preferred_payment_method}
              onChange={(e) => setFormData({ ...formData, preferred_payment_method: e.target.value })}
              placeholder="e.g., Bank Transfer, UPI, Check"
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-foreground/80 mb-1">Address</label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-foreground/80 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={handleCloseClick}
            className="px-6 py-2 border border-input rounded-lg hover:bg-muted/50 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition disabled:opacity-50"
          >
            {saving ? 'Saving...' : vendor ? 'Update Vendor' : 'Create Vendor'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default VendorsPage;
